import { z } from 'zod';
import { GenderSchema } from './employee.schema.js';

export const AccrualMethodSchema = z.enum(['UPFRONT', 'MONTHLY', 'YEARLY']);

export const LeaveTypeInputSchema = z.object({
  name: z.string().min(1).max(120),
  code: z.string().min(1).max(40),
  daysPerYear: z.number().nonnegative().max(365),
  isPaid: z.boolean().default(true),
  accrualMethod: AccrualMethodSchema.default('YEARLY'),
  carryForward: z.boolean().default(false),
  carryForwardCap: z.number().nonnegative().optional().nullable(),
  requiresDocument: z.boolean().default(false),
  minNoticeDays: z.number().int().min(0).max(365).default(0),
  maxConsecutive: z.number().int().min(0).optional().nullable(),
  applicableGender: GenderSchema.optional().nullable(),
  probationAllowed: z.boolean().default(false),
  isActive: z.boolean().default(true),
  color: z.string().default('#3B82F6'),
  description: z.string().max(2000).optional().nullable(),
});
export type LeaveTypeInput = z.infer<typeof LeaveTypeInputSchema>;

export const HalfDaySessionSchema = z.enum(['MORNING', 'AFTERNOON']);

export const LeaveRequestInputSchema = z
  .object({
    leaveTypeId: z.string().min(1),
    startDate: z.string().date(),
    endDate: z.string().date(),
    isHalfDay: z.boolean().default(false),
    halfDaySession: HalfDaySessionSchema.optional().nullable(),
    reason: z.string().min(5).max(2000),
    contactDuringLeave: z.string().max(120).optional().nullable(),
    documentUrl: z.string().max(500).optional().nullable(),
  })
  // ISO `YYYY-MM-DD` strings compare correctly with `>=` for ordering.
  .refine((d) => d.endDate >= d.startDate, {
    message: 'End date must be on or after the start date.',
    path: ['endDate'],
  })
  // A half-day can only apply to a single calendar day. Without this guard a
  // multi-day range with `isHalfDay:true` was booked as 0.5 days (the day-count
  // only reads the start day), silently under-charging the balance vs. the
  // multi-day preview the employee saw. Reject it at the boundary instead.
  .refine((d) => !d.isHalfDay || d.startDate === d.endDate, {
    message: 'A half-day request must start and end on the same date.',
    path: ['isHalfDay'],
  });
export type LeaveRequestInput = z.infer<typeof LeaveRequestInputSchema>;

export const LeaveDecisionSchema = z.object({
  rejectionReason: z.string().max(500).optional().nullable(),
});
export type LeaveDecision = z.infer<typeof LeaveDecisionSchema>;

export const HolidayTypeSchema = z.enum(['PUBLIC', 'RELIGIOUS', 'COMPANY', 'OPTIONAL']);

export const HolidayInputSchema = z.object({
  name: z.string().min(1).max(120),
  date: z.string().date(),
  type: HolidayTypeSchema.default('PUBLIC'),
  isRecurring: z.boolean().default(false),
  description: z.string().max(500).optional().nullable(),
});
export type HolidayInput = z.infer<typeof HolidayInputSchema>;
